// Import Statements
import React, { useState, useEffect, useReducer, useCallback } from "react";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import {
  TruckIcon,
  ShoppingCartIcon,
  BeakerIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  SunIcon,
  MoonIcon,
  DocumentTextIcon,
  LockClosedIcon,
  UsersIcon,
  GlobeAmericasIcon, // Replaced LeafIcon with GlobeAmericasIcon
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Simulated Hedera SDK with Advanced Tokenization
const hedera = {
  tokenizeProduct: async (product) => {
    if (!product.productName || !product.quantity) throw new Error("Invalid product data");
    return {
      tokenId: `0.0.${Date.now()}`,
      transactionId: `tx-${Date.now()}`,
      nftCertificate: {
        id: `NFT-${Date.now()}`,
        metadata: {
          name: `${product.productName} Token`,
          description: `Tokenized ${product.quantity} units from ${product.location}, harvested on ${product.harvestDate}`,
          attributes: [
            { trait_type: "Crop", value: product.productName },
            { trait_type: "Quantity", value: product.quantity },
            { trait_type: "Sustainability", value: product.sustainabilityScore },
            { trait_type: "Insured", value: product.insured ? "Yes" : "No" },
          ],
          image: `https://example.com/nft/${product.productName.toLowerCase()}.png`,
          timestamp: new Date().toISOString(),
        },
      },
    };
  },
  logToHCS: async (message) => ({ messageId: `hcs-${Date.now()}` }),
  transferHBAR: async (amount) => {
    if (amount <= 0) throw new Error("Invalid transfer amount");
    return { success: true, receipt: `receipt-${Date.now()}` };
  },
  stakeToken: async (tokenId, amount) => {
    if (amount <= 0) throw new Error("Invalid stake amount");
    return { success: true, reward: amount * 0.15, stakeId: `stake-${Date.now()}` };
  },
  burnToken: async (tokenId) => ({ success: true, txId: `burn-${Date.now()}` }),
};

// Simulated Chainlink Data Feeds
const fetchWeatherData = async () => {
  try {
    return {
      rainfall: (Math.random() * 20).toFixed(1),
      temperature: (15 + Math.random() * 20).toFixed(1),
      humidity: (30 + Math.random() * 50).toFixed(0),
      windSpeed: (5 + Math.random() * 15).toFixed(1),
    };
  } catch (e) {
    throw new Error("Weather data fetch failed");
  }
};

const fetchMarketPrices = async () => {
  const crops = ["Maize", "Beans", "Tea", "Coffee"];
  const prices = {};
  for (const crop of crops) {
    const basePrice = { Maize: 0.6, Beans: 0.5, Tea: 0.8, Coffee: 1.2 }[crop];
    const fluctuation = (Math.random() * 0.4 - 0.2).toFixed(2);
    prices[crop.toLowerCase() + "Price"] = Math.max(0.1, basePrice + parseFloat(fluctuation)).toFixed(2);
  }
  return prices;
};

// Advanced AI Engine
const analyzeSoil = async ({ ph, nitrogen, potassium, phosphorus, organicMatter }) => {
  if ([ph, nitrogen, potassium, phosphorus, organicMatter].some(v => isNaN(v) || v < 0)) {
    throw new Error("Invalid soil parameters");
  }
  const suitabilityScore = (
    (ph >= 6 && ph <= 7.5 ? 20 : 10) +
    (nitrogen > 25 ? 20 : 10) +
    (potassium > 20 ? 20 : 10) +
    (phosphorus > 15 ? 20 : 10) +
    (organicMatter > 2 ? 20 : 10)
  );
  return {
    score: suitabilityScore,
    recommendation:
      suitabilityScore > 80 ? "Excellent for high-yield crops like Tea or Coffee."
      : suitabilityScore > 60 ? "Good for Maize or Beans."
      : "Soil needs amendments for optimal growth.",
  };
};

const predictYield = (soilData, weatherData, crop) => {
  const { score = 50 } = soilData || {};
  const { rainfall = 10, temperature = 25, humidity = 50 } = weatherData || {};
  const cropFactors = {
    maize: { rain: 10, temp: 25, humid: 60, baseYield: 120 },
    beans: { rain: 8, temp: 22, humid: 55, baseYield: 90 },
    tea: { rain: 12, temp: 20, humid: 70, baseYield: 150 },
    coffee: { rain: 15, temp: 23, humid: 65, baseYield: 200 },
  };
  const { rain, temp, humid, baseYield } = cropFactors[crop.toLowerCase()] || cropFactors.maize;
  const rainFactor = Math.min(1.2, rainfall / rain);
  const tempFactor = Math.min(1, 1 - Math.abs(temperature - temp) / 15);
  const humidFactor = Math.min(1, humidity / humid);
  return Math.round(baseYield * (score / 100) * rainFactor * tempFactor * humidFactor);
};

const getAICropRecommendation = (soilData, weatherData, marketData, historicalYields) => {
  const crops = ["Maize", "Beans", "Tea", "Coffee"];
  const evaluations = crops.map(crop => {
    const cropYield = predictYield(soilData, weatherData, crop);
    const price = parseFloat(marketData[crop.toLowerCase() + "Price"] || 0.5);
    const historicalAvg = historicalYields[crop.toLowerCase()]?.average || cropYield;
    const profitPotential = cropYield * price * 0.9; // 10% market fee
    return { crop, cropYield, profitPotential, historicalAvg };
  });
  const bestCrop = evaluations.reduce((best, curr) => curr.profitPotential > best.profitPotential ? curr : best, evaluations[0]);
  return {
    recommendation: `${bestCrop.crop} recommended. Expected yield: ${bestCrop.cropYield} units, Profit: ${bestCrop.profitPotential.toFixed(2)} HBAR.`,
    details: evaluations,
  };
};

const calculateCarbonFootprint = (distance, quantity, method = "truck") => {
  const factor = method === "truck" ? 0.12 : method === "rail" ? 0.05 : 0.1;
  return (distance * factor * quantity / 100).toFixed(2);
};

// Chart.js Registration
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend);

// Animation Variants
const sectionVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.15, duration: 0.6 } }),
};

// State Reducer with Mocked Data
const initialState = {
  users: [
    { email: "john@farmer.com", password: "password123", role: "farmer", agriPoints: 150, badges: ["Eco-Friendly"], createdAt: "2025-01-01T00:00:00Z" },
    { email: "aisha@buyer.com", password: "password123", role: "buyer", agriPoints: 50, badges: ["Top Buyer"], createdAt: "2025-01-02T00:00:00Z" },
  ],
  crops: [
    {
      productName: "Maize",
      quantity: 100,
      location: "Nairobi",
      harvestDate: "2025-01-15",
      tokenId: "0.0.123456",
      nftCertificate: { id: "NFT-123456", metadata: { name: "Maize Token", description: "Tokenized 100 units from Nairobi, harvested on 2025-01-15", attributes: [] } },
      insured: true,
      sustainabilityScore: 85,
      status: "Available",
      logistics: [{ stage: "Harvested", timestamp: "2025-01-15T00:00:00Z", location: "Nairobi" }],
      stakedAmount: 20,
      owner: "john@farmer.com",
      createdAt: "2025-01-15T00:00:00Z",
    },
    {
      productName: "Coffee",
      quantity: 50,
      location: "Kilimanjaro",
      harvestDate: "2025-02-01",
      tokenId: "0.0.123457",
      nftCertificate: { id: "NFT-123457", metadata: { name: "Coffee Token", description: "Tokenized 50 units from Kilimanjaro, harvested on 2025-02-01", attributes: [] } },
      insured: false,
      sustainabilityScore: 60,
      status: "Sold",
      logistics: [{ stage: "Delivered", timestamp: "2025-02-10T00:00:00Z", location: "Dar es Salaam" }],
      stakedAmount: 0,
      owner: "john@farmer.com",
      buyer: "aisha@buyer.com",
      carbonFootprint: "12.00",
      createdAt: "2025-02-01T00:00:00Z",
    },
  ],
  messages: [
    { sender: "john@farmer.com", content: "Looking for buyers for my Maize!", timestamp: "2025-01-15T10:00:00Z" },
    { sender: "aisha@buyer.com", content: "Interested in your Coffee. Let’s trade!", timestamp: "2025-02-01T12:00:00Z" },
  ],
  agriPoints: 200,
  greenCredits: 150,
  walletBalance: 500,
  transactions: [
    { message: "Tokenize - Maize", txId: "tx-123456", timestamp: "2025-01-15T00:00:00Z" },
    { message: "Buy - Coffee for 60 HBAR", txId: "receipt-123457", timestamp: "2025-02-10T00:00:00Z" },
  ],
  soilData: { ph: 6.8, nitrogen: 30, potassium: 25, phosphorus: 20, organicMatter: 3, score: 90, recommendation: "Excellent for high-yield crops like Tea or Coffee." },
  marketData: {
    weather: "Sunny, 25°C",
    rainfall: "12.5",
    temperature: "25",
    humidity: "65",
    windSpeed: "8",
    maizePrice: "0.65",
    beansPrice: "0.55",
    teaPrice: "0.85",
    coffeePrice: "1.25",
  },
  analytics: {
    rainfallHistory: [10, 12, 15, 8, 14],
    priceHistory: {
      maize: [0.6, 0.62, 0.65, 0.64, 0.65],
      beans: [0.5, 0.52, 0.55, 0.53, 0.55],
      tea: [0.8, 0.82, 0.85, 0.84, 0.85],
      coffee: [1.2, 1.22, 1.25, 1.23, 1.25],
    },
    yieldHistory: {
      maize: { total: 1200, count: 10, average: 120 },
      beans: { total: 900, count: 10, average: 90 },
      tea: { total: 1500, count: 10, average: 150 },
      coffee: { total: 2000, count: 10, average: 200 },
    },
    carbonFootprint: 25,
  },
  settings: { notifications: true, autoStake: false },
};

const reducer = (state, action) => {
  const newState = (() => {
    switch (action.type) {
      case "ADD_USER": return { ...state, users: [...state.users, action.payload] };
      case "UPDATE_USER": return { ...state, users: state.users.map(u => u.email === action.payload.email ? action.payload : u) };
      case "ADD_CROP": return { ...state, crops: [...state.crops, action.payload], agriPoints: state.agriPoints + 25, greenCredits: state.greenCredits + 15 };
      case "UPDATE_CROP": return { ...state, crops: state.crops.map((c, i) => i === action.index ? action.payload : c) };
      case "ADD_MESSAGE": return { ...state, messages: [...state.messages, action.payload] };
      case "UPDATE_BALANCE": return { ...state, walletBalance: Math.max(0, state.walletBalance + action.amount) }; // Fixed: Added dot between state and walletBalance
      case "ADD_TRANSACTION": return { ...state, transactions: [...state.transactions, action.payload] };
      case "SET_SOIL_DATA": return { ...state, soilData: action.payload };
      case "UPDATE_MARKET_DATA": return { ...state, marketData: action.payload };
      case "UPDATE_ANALYTICS": return { ...state, analytics: action.payload };
      case "UPDATE_SETTINGS": return { ...state, settings: action.payload };
      default: return state;
    }
  })();
  Object.entries(newState).forEach(([key, value]) => localStorage.setItem(key, JSON.stringify(value)));
  return newState;
};

// Main App Component
function App() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [language, setLanguage] = useState("en");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [isLoading, setIsLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");

  const translations = {
    en: {
      title: "AgriFuture",
      welcome: "Welcome to AgriFuture",
      subtitle: "Empowering Sustainable Agriculture with Blockchain Technology",
      getStarted: "Get Started",
      login: "Login",
      signup: "Sign Up",
      logout: "Logout",
      email: "Email",
      password: "Password",
      role: "Role",
      farmer: "Farmer",
      buyer: "Buyer",
      dashboard: "Dashboard",
      tokenize: "Tokenize Crop",
      marketplace: "Marketplace",
      p2pTrading: "P2P Trading",
      logistics: "Logistics",
      insurance: "Insurance",
      aiInsights: "AI Insights",
      soilAnalysis: "Soil Analysis",
      staking: "Token Staking",
      chat: "Community Chat",
      carbonOffset: "Carbon Offset",
      analytics: "Analytics",
      settings: "Settings",
      noData: "No data available",
      productName: "Product Name",
      quantity: "Quantity",
      location: "Location",
      harvestDate: "Harvest Date",
      sustainabilityScore: "Sustainability Score",
      insured: "Insured",
      submit: "Submit",
      buy: "Buy",
      sell: "Sell",
      price: "Price",
      distance: "Distance (km)",
      method: "Transport Method",
      truck: "Truck",
      rail: "Rail",
      stake: "Stake",
      burnToken: "Burn Token",
      sendMessage: "Send",
      offsetCarbon: "Offset Carbon",
      toggleNotifications: "Toggle Notifications",
      autoStake: "Auto-Stake Tokens",
      feature1Title: "Tokenize Your Crops",
      feature1Desc: "Convert your harvest into digital assets on Hedera.",
      feature2Title: "Secure Marketplace",
      feature2Desc: "Trade tokenized crops with confidence.",
      feature3Title: "AI-Powered Insights",
      feature3Desc: "Optimize yields with AI recommendations.",
      feature4Title: "Transparent Logistics",
      feature4Desc: "Track your goods in real-time.",
      feature5Title: "Insurance Protection",
      feature5Desc: "Protect your crops with smart contracts.",
      feature6Title: "Carbon Offset",
      feature6Desc: "Contribute to sustainability with carbon credits.",
      ctaTitle: "Join the Future of Agriculture",
      ctaDesc: "Sign up today and start tokenizing your crops!",
    },
    fr: {
      title: "AgriFuture",
      welcome: "Bienvenue chez AgriFuture",
      subtitle: "Renforcer l'agriculture durable avec la technologie blockchain",
      getStarted: "Commencer",
      login: "Connexion",
      signup: "Inscription",
      logout: "Déconnexion",
      email: "Email",
      password: "Mot de passe",
      role: "Rôle",
      farmer: "Agriculteur",
      buyer: "Acheteur",
      dashboard: "Tableau de bord",
      tokenize: "Tokeniser la récolte",
      marketplace: "Marché",
      p2pTrading: "Commerce P2P",
      logistics: "Logistique",
      insurance: "Assurance",
      aiInsights: "Insights IA",
      soilAnalysis: "Analyse du sol",
      staking: "Mise de jetons",
      chat: "Chat communautaire",
      carbonOffset: "Compensation carbone",
      analytics: "Analytique",
      settings: "Paramètres",
      noData: "Aucune donnée disponible",
      productName: "Nom du produit",
      quantity: "Quantité",
      location: "Emplacement",
      harvestDate: "Date de récolte",
      sustainabilityScore: "Score de durabilité",
      insured: "Assuré",
      submit: "Soumettre",
      buy: "Acheter",
      sell: "Vendre",
      price: "Prix",
      distance: "Distance (km)",
      method: "Méthode de transport",
      truck: "Camion",
      rail: "Rail",
      stake: "Miser",
      burnToken: "Brûler le jeton",
      sendMessage: "Envoyer",
      offsetCarbon: "Compenser le carbone",
      toggleNotifications: "Activer les notifications",
      autoStake: "Mise automatique des jetons",
      feature1Title: "Tokenisez vos récoltes",
      feature1Desc: "Convertissez votre récolte en actifs numériques sur Hedera.",
      feature2Title: "Marché sécurisé",
      feature2Desc: "Échangez des récoltes tokenisées en toute confiance.",
      feature3Title: "Insights alimentés par l'IA",
      feature3Desc: "Optimisez les rendements avec des recommandations IA.",
      feature4Title: "Logistique transparente",
      feature4Desc: "Suivez vos marchandises en temps réel.",
      feature5Title: "Protection par assurance",
      feature5Desc: "Protégez vos récoltes avec des contrats intelligents.",
      feature6Title: "Compensation carbone",
      feature6Desc: "Contribuez à la durabilité avec des crédits carbone.",
      ctaTitle: "Rejoignez l'avenir de l'agriculture",
      ctaDesc: "Inscrivez-vous aujourd'hui et commencez à tokeniser vos récoltes !",
    },
  };

  const t = translations[language];

  // Error Boundary Component
  const ErrorBoundary = ({ children }) => {
    const [hasError, setHasError] = useState(false);
    if (hasError) return <div className="p-4 text-red-500">Something went wrong. Please try again.</div>;
    return <>{children}</>;
  };

  // Header Component
  const Header = () => (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 z-50 ${darkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"} shadow-md`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
        <h1 className="text-2xl font-bold">{t.title}</h1>
        <div className="flex items-center space-x-4">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className={`p-2 rounded-lg ${darkMode ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-900"}`}
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
          <button onClick={() => setDarkMode(!darkMode)} className="p-2 rounded-full hover:bg-gray-200">
            {darkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
          </button>
          {isLoggedIn ? (
            <button onClick={handleLogout} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">
              {t.logout}
            </button>
          ) : (
            <button onClick={() => setShowAuthModal(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">
              {t.login}
            </button>
          )}
        </div>
      </div>
    </motion.header>
  );

    // Auth Modal Component
    const AuthModal = () => {
      const { register, handleSubmit, reset, formState: { errors } } = useForm();
      const onSubmit = (data) => {
        if (authMode === "signup") {
          if (state.users.some(u => u.email === data.email)) return toast.error("User already exists");
          const newUser = { ...data, agriPoints: 0, badges: [], createdAt: new Date().toISOString() };
          dispatch({ type: "ADD_USER", payload: newUser });
          toast.success("Sign up successful! Please log in.");
          setAuthMode("login");
        } else {
          const user = state.users.find(u => u.email === data.email && u.password === data.password);
          if (!user) return toast.error("Invalid credentials");
          setIsLoggedIn(true);
          setCurrentUser(user);
          setShowAuthModal(false);
          toast.success(`Welcome, ${user.email}!`);
        }
        reset();
      };
  
      return (
        <AnimatePresence>
          {showAuthModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-md w-full`}
              >
                <h2 className="text-2xl font-bold mb-6">{authMode === "signup" ? t.signup : t.login}</h2>
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <input
                      {...register("email", { required: "Email is required", pattern: { value: /^\S+@\S+$/i, message: "Invalid email" } })}
                      placeholder={t.email}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {errors.email && <p className="text-red-500 text-sm">{errors.email.message}</p>}
                  </div>
                  <div>
                    <input
                      {...register("password", { required: "Password is required", minLength: { value: 6, message: "Password must be at least 6 characters" } })}
                      type="password"
                      placeholder={t.password}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    {errors.password && <p className="text-red-500 text-sm">{errors.password.message}</p>}
                  </div>
                  {authMode === "signup" && (
                    <div>
                      <select {...register("role", { required: "Role is required" })} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                        <option value="">{t.role}</option>
                        <option value="farmer">{t.farmer}</option>
                        <option value="buyer">{t.buyer}</option>
                      </select>
                      {errors.role && <p className="text-red-500 text-sm">{errors.role.message}</p>}
                    </div>
                  )}
                  <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700">{authMode === "signup" ? t.signup : t.login}</button>
                </form>
                <button onClick={() => setShowAuthModal(false)} className="mt-4 text-gray-500 hover:underline">Close</button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      );
    };
  
    // Enhanced Landing Page
    const ShowcaseLandingPage = () => (
      <motion.div initial="hidden" animate="visible" className="text-center">
        <motion.h1 variants={sectionVariants} custom={0} className="text-5xl font-extrabold mb-6">{t.welcome}</motion.h1>
        <motion.p variants={sectionVariants} custom={1} className="text-xl mb-8">{t.subtitle}</motion.p>
        <motion.div variants={sectionVariants} custom={2} className="mb-12">
          <button onClick={() => { setAuthMode("signup"); setShowAuthModal(true); }} className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center mx-auto">
            {t.ctaTitle} <ArrowRightIcon className="h-5 w-5 ml-2" />
          </button>
        </motion.div>
        <motion.div variants={sectionVariants} custom={3} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {[
            { icon: GlobeAltIcon, title: t.feature1Title, desc: t.feature1Desc },
            { icon: ShoppingCartIcon, title: t.feature2Title, desc: t.feature2Desc },
            { icon: SparklesIcon, title: t.feature3Title, desc: t.feature3Desc },
            { icon: TruckIcon, title: t.feature4Title, desc: t.feature4Desc },
            { icon: ShieldCheckIcon, title: t.feature5Title, desc: t.feature5Desc },
            { icon: GlobeAmericasIcon, title: t.feature6Title, desc: t.feature6Desc }, // Replaced LeafIcon with GlobeAmericasIcon
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              variants={sectionVariants}
              custom={4 + idx}
              className={`p-6 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300`}
            >
              <feature.icon className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
              <p>{feature.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    );
  
    // Dashboard Component
    const Dashboard = () => (
      <motion.div variants={sectionVariants} custom={2} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className={`p-6 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
          <h3 className="text-xl font-bold mb-4">Wallet</h3>
          <p>Balance: {state.walletBalance.toFixed(2)} HBAR</p>
          <p>AgriPoints: {state.agriPoints}</p>
          <p>Green Credits: {state.greenCredits}</p>
        </div>
        <div className={`p-6 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
          <h3 className="text-xl font-bold mb-4">Market Data</h3>
          <p>Weather: {state.marketData.weather}</p>
          <p>Maize: {state.marketData.maizePrice} HBAR/unit</p>
          <p>Beans: {state.marketData.beansPrice} HBAR/unit</p>
          <p>Tea: {state.marketData.teaPrice} HBAR/unit</p>
          <p>Coffee: {state.marketData.coffeePrice} HBAR/unit</p>
        </div>
        <div className={`p-6 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
          <h3 className="text-xl font-bold mb-4">Recent Transactions</h3>
          {state.transactions.length === 0 ? (
            <p>{t.noData}</p>
          ) : (
            state.transactions.slice(-3).map((tx, idx) => (
              <p key={idx} className="text-sm">{tx.message} - {new Date(tx.timestamp).toLocaleString()}</p>
            ))
          )}
        </div>
      </motion.div>
    );
  
    // Tokenize Crop Component
    const TokenizeCrop = () => {
      const { register, handleSubmit, reset, formState: { errors } } = useForm();
      const onSubmit = async (data) => {
        if (!isLoggedIn || currentUser.role !== "farmer") return toast.error("Restricted to farmers");
        try {
          const { tokenId, transactionId, nftCertificate } = await hedera.tokenizeProduct(data);
          const cropData = {
            ...data,
            tokenId,
            nftCertificate,
            insured: data.insured === "yes",
            sustainabilityScore: parseInt(data.sustainabilityScore),
            status: "Available",
            logistics: [{ stage: "Harvested", timestamp: new Date().toISOString(), location: data.location }],
            stakedAmount: 0,
            owner: currentUser.email,
            createdAt: new Date().toISOString(),
          };
          dispatch({ type: "ADD_CROP", payload: cropData });
          dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.tokenize} - ${data.productName}`, txId: transactionId, timestamp: new Date().toISOString() } });
          toast.success(`Crop tokenized! Token ID: ${tokenId}`);
          reset();
        } catch (err) {
          toast.error(`Tokenization failed: ${err.message}`);
        }
      };
  
      return (
        <motion.div variants={sectionVariants} custom={3} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
          <div className="flex items-center mb-6">
            <GlobeAltIcon className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-3xl font-bold">{t.tokenize}</h3>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input {...register("productName", { required: "Product name is required" })} placeholder={t.productName} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.productName && <p className="text-red-500 text-sm">{errors.productName.message}</p>}
            <input {...register("quantity", { required: "Quantity is required", min: { value: 1, message: "Minimum 1 unit" } })} type="number" placeholder={t.quantity} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.quantity && <p className="text-red-500 text-sm">{errors.quantity.message}</p>}
            <input {...register("location", { required: "Location is required" })} placeholder={t.location} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.location && <p className="text-red-500 text-sm">{errors.location.message}</p>}
            <input {...register("harvestDate", { required: "Harvest date is required" })} type="date" placeholder={t.harvestDate} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.harvestDate && <p className="text-red-500 text-sm">{errors.harvestDate.message}</p>}
            <input {...register("sustainabilityScore", { required: "Sustainability score is required", min: 0, max: 100 })} type="number" placeholder={t.sustainabilityScore} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.sustainabilityScore && <p className="text-red-500 text-sm">{errors.sustainabilityScore.message}</p>}
            <select {...register("insured", { required: "Insurance status is required" })} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="">{t.insured}</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {errors.insured && <p className="text-red-500 text-sm">{errors.insured.message}</p>}
            <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700">{t.submit}</button>
          </form>
        </motion.div>
      );
    };
  
    // Marketplace Component
    const Marketplace = () => {
      const handleBuy = async (crop) => {
        if (!isLoggedIn || currentUser.role !== "buyer") return toast.error("Restricted to buyers");
        if (crop.status !== "Available") return toast.error("Crop not available");
        const price = parseFloat(state.marketData[`${crop.productName.toLowerCase()}Price`]) * crop.quantity;
        if (price > state.walletBalance) return toast.error("Insufficient funds");
        try {
          const { success, receipt } = await hedera.transferHBAR(price);
          if (!success) throw new Error("Purchase failed");
          const cropIndex = state.crops.indexOf(crop);
          dispatch({
            type: "UPDATE_CROP",
            index: cropIndex,
            payload: { ...crop, status: "Sold", buyer: currentUser.email, carbonFootprint: calculateCarbonFootprint(100, crop.quantity) },
          });
          dispatch({ type: "UPDATE_BALANCE", amount: -price });
          dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.buy} - ${crop.productName} for ${price.toFixed(2)} HBAR`, txId: receipt, timestamp: new Date().toISOString() } });
          toast.success(`Purchased ${crop.productName} for ${price.toFixed(2)} HBAR`);
        } catch (err) {
          toast.error(`Purchase failed: ${err.message}`);
        }
      };
  
      return (
        <motion.div variants={sectionVariants} custom={4} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
          <div className="flex items-center mb-6">
            <ShoppingCartIcon className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-3xl font-bold">{t.marketplace}</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {state.crops.filter(c => c.status === "Available").length === 0 ? (
              <p className="text-center col-span-full">{t.noData}</p>
            ) : (
              state.crops.filter(c => c.status === "Available").map((crop, idx) => (
                <div key={idx} className={`p-4 ${darkMode ? "bg-gray-600" : "bg-gray-50"} rounded-lg`}>
                  <p>{crop.productName} - {crop.quantity} units</p>
                  <p>Location: {crop.location}</p>
                  <p>Price: {(parseFloat(state.marketData[`${crop.productName.toLowerCase()}Price`]) * crop.quantity).toFixed(2)} HBAR</p>
                  <button onClick={() => handleBuy(crop)} className="mt-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">{t.buy}</button>
                </div>
              ))
            )}
          </div>
        </motion.div>
      );
    };
  
    // P2P Trading Component
    const P2PTrading = () => {
      const { register, handleSubmit, reset, formState: { errors } } = useForm();
      const onSubmit = async (data) => {
        if (!isLoggedIn) return toast.error("Login required");
        const crop = state.crops.find(c => c.tokenId === data.tokenId && c.owner !== currentUser.email && c.status === "Available");
        if (!crop) return toast.error("Invalid or unauthorized token");
        const price = parseFloat(data.price);
        if (price > state.walletBalance) return toast.error("Insufficient funds");
        try {
          const { success, receipt } = await hedera.transferHBAR(price);
          if (!success) throw new Error("Trade failed");
          const cropIndex = state.crops.indexOf(crop);
          dispatch({
            type: "UPDATE_CROP",
            index: cropIndex,
            payload: { ...crop, status: "Sold", buyer: currentUser.email, carbonFootprint: calculateCarbonFootprint(100, crop.quantity) },
          });
          dispatch({ type: "UPDATE_BALANCE", amount: -price });
          dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.sell} - ${crop.productName} for ${price.toFixed(2)} HBAR`, txId: receipt, timestamp: new Date().toISOString() } });
          toast.success(`Traded ${crop.productName} for ${price.toFixed(2)} HBAR`);
          reset();
        } catch (err) {
          toast.error(`Trade failed: ${err.message}`);
        }
      };
  
      return (
        <motion.div variants={sectionVariants} custom={5} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
          <div className="flex items-center mb-6">
            <ShoppingCartIcon className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-3xl font-bold">{t.p2pTrading}</h3>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <input {...register("tokenId", { required: "Token ID is required" })} placeholder="Token ID" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.tokenId && <p className="text-red-500 text-sm">{errors.tokenId.message}</p>}
            <input {...register("price", { required: "Price is required", min: { value: 0.1, message: "Minimum 0.1 HBAR" } })} type="number" step="0.01" placeholder="Price (HBAR)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.price && <p className="text-red-500 text-sm">{errors.price.message}</p>}
            <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700">{t.sell}</button>
          </form>
        </motion.div>
      );
    };
  
    // Logistics Component
    const Logistics = () => {
      const { register, handleSubmit, reset, formState: { errors } } = useForm();
      const onSubmit = async (data) => {
        if (!isLoggedIn) return toast.error("Login required");
        const crop = state.crops.find(c => c.tokenId === data.tokenId && (c.owner === currentUser.email || c.buyer === currentUser.email));
        if (!crop) return toast.error("Invalid or unauthorized token");
        try {
          const { messageId } = await hedera.logToHCS(`Logistics Update: ${data.stage} at ${data.location}`);
          const updatedLogistics = [...crop.logistics, { stage: data.stage, timestamp: new Date().toISOString(), location: data.location }];
          const carbonFootprint = calculateCarbonFootprint(parseFloat(data.distance), crop.quantity, data.method);
          const cropIndex = state.crops.indexOf(crop);
          dispatch({
            type: "UPDATE_CROP",
            index: cropIndex,
            payload: { ...crop, logistics: updatedLogistics, carbonFootprint },
          });
          dispatch({ type: "ADD_TRANSACTION", payload: { message: `Logistics - ${data.stage} at ${data.location}`, txId: messageId, timestamp: new Date().toISOString() } });
          toast.success(`Logistics updated! Carbon Footprint: ${carbonFootprint} tons`);
          reset();
        } catch (err) {
          toast.error(`Logistics update failed: ${err.message}`);
        }
      };
  
      return (
        <motion.div variants={sectionVariants} custom={6} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
          <div className="flex items-center mb-6">
            <TruckIcon className="h-8 w-8 text-green-600 mr-3" />
            <h3 className="text-3xl font-bold">{t.logistics}</h3>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mb-6">
            <input {...register("tokenId", { required: "Token ID is required" })} placeholder="Token ID" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.tokenId && <p className="text-red-500 text-sm">{errors.tokenId.message}</p>}
            <input {...register("stage", { required: "Stage is required" })} placeholder="Stage (e.g., Shipped)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.stage && <p className="text-red-500 text-sm">{errors.stage.message}</p>}
            <input {...register("location", { required: "Location is required" })} placeholder={t.location} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.location && <p className="text-red-500 text-sm">{errors.location.message}</p>}
            <input {...register("distance", { required: "Distance is required", min: { value: 1, message: "Minimum 1 km" } })} type="number" placeholder={t.distance} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
            {errors.distance && <p className="text-red-500 text-sm">{errors.distance.message}</p>}
            <select {...register("method", { required: "Transport method is required" })} className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
              <option value="">{t.method}</option>
              <option value="truck">{t.truck}</option>
              <option value="rail">{t.rail}</option>
            </select>
            {errors.method && <p className="text-red-500 text-sm">{errors.method.message}</p>}
            <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700">{t.submit}</button>
          </form>
          <div>
            <h4 className="text-xl font-semibold mb-4">Logistics Tracking</h4>
            {state.crops.filter(c => c.owner === currentUser?.email || c.buyer === currentUser?.email).length === 0 ? (
              <p className="text-center">{t.noData}</p>
            ) : (
              state.crops.filter(c => c.owner === currentUser?.email || c.buyer === currentUser?.email).map((crop, idx) => (
                <div key={idx} className={`p-4 ${darkMode ? "bg-gray-600" : "bg-gray-50"} rounded-lg mb-4`}>
                  <p>Token ID: {crop.tokenId}</p>
                  <p>Product: {crop.productName}</p>
                  {crop.logistics.map((log, i) => (
                    <p key={i} className="text-sm">{log.stage} - {log.location} on {new Date(log.timestamp).toLocaleString()}</p>
                  ))}
                  {crop.carbonFootprint && <p>Carbon Footprint: {crop.carbonFootprint} tons</p>}
                </div>
              ))
            )}
          </div>
        </motion.div>
      );
    };
  
     // Insurance Component
  const Insurance = () => {
    const handleInsure = async (crop) => {
      if (!isLoggedIn || currentUser.role !== "farmer") return toast.error("Restricted to farmers");
      if (crop.insured) return toast.info("Crop already insured");
      const premium = crop.quantity * 0.05; // 0.05 HBAR per unit
      if (premium > state.walletBalance) return toast.error("Insufficient funds");
      try {
        const { success, receipt } = await hedera.transferHBAR(premium);
        if (!success) throw new Error("Insurance failed");
        const cropIndex = state.crops.indexOf(crop);
        dispatch({
          type: "UPDATE_CROP",
          index: cropIndex,
          payload: { ...crop, insured: true },
        });
        dispatch({ type: "UPDATE_BALANCE", amount: -premium });
        dispatch({ type: "ADD_TRANSACTION", payload: { message: `Insurance - ${crop.productName} for ${premium.toFixed(2)} HBAR`, txId: receipt, timestamp: new Date().toISOString() } });
        toast.success(`Insured ${crop.productName} for ${premium.toFixed(2)} HBAR`);
      } catch (err) {
        toast.error(`Insurance failed: ${err.message}`);
      }
    };

    return (
      <motion.div variants={sectionVariants} custom={7} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
        <div className="flex items-center mb-6">
          <ShieldCheckIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.insurance}</h3>
        </div>
        {state.crops.filter(c => c.owner === currentUser?.email && !c.insured).length === 0 ? (
          <p className="text-center">{t.noData}</p>
        ) : (
          state.crops.filter(c => c.owner === currentUser?.email && !c.insured).map((crop, idx) => (
            <div key={idx} className={`p-4 ${darkMode ? "bg-gray-600" : "bg-gray-50"} rounded-lg mb-4`}>
              <p>{crop.productName} - {crop.quantity} units</p>
              <p>Premium: {(crop.quantity * 0.05).toFixed(2)} HBAR</p>
              <button onClick={() => handleInsure(crop)} className="mt-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">{t.insured}</button>
            </div>
          ))
        )}
      </motion.div>
    );
  };

  // AI Insights Component
  const AIInsights = () => {
    const recommendation = getAICropRecommendation(state.soilData, state.marketData, state.marketData, state.analytics.yieldHistory);
    return (
      <motion.div variants={sectionVariants} custom={8} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
        <div className="flex items-center mb-6">
          <SparklesIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.aiInsights}</h3>
        </div>
        <p className="text-lg mb-4">{recommendation.recommendation}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendation.details.map((detail, idx) => (
            <div key={idx} className={`p-4 ${darkMode ? "bg-gray-600" : "bg-gray-50"} rounded-lg`}>
              <p>Crop: {detail.crop}</p>
              <p>Expected Yield: {detail.cropYield} units</p>
              <p>Profit Potential: {detail.profitPotential.toFixed(2)} HBAR</p>
              <p>Historical Avg: {detail.historicalAvg} units</p>
            </div>
          ))}
        </div>
      </motion.div>
    );
  };

  // Soil Analysis Component
  const SoilAnalysis = () => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const onSubmit = async (data) => {
      if (!isLoggedIn || currentUser.role !== "farmer") return toast.error("Restricted to farmers");
      try {
        const soilData = await analyzeSoil({
          ph: parseFloat(data.ph),
          nitrogen: parseFloat(data.nitrogen),
          potassium: parseFloat(data.potassium),
          phosphorus: parseFloat(data.phosphorus),
          organicMatter: parseFloat(data.organicMatter),
        });
        dispatch({ type: "SET_SOIL_DATA", payload: soilData });
        toast.success("Soil analysis completed!");
        reset();
      } catch (err) {
        toast.error(`Soil analysis failed: ${err.message}`);
      }
    };

    return (
      <motion.div variants={sectionVariants} custom={9} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
        <div className="flex items-center mb-6">
          <BeakerIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.soilAnalysis}</h3>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input {...register("ph", { required: "pH is required", min: 0, max: 14 })} type="number" step="0.1" placeholder="pH Level" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.ph && <p className="text-red-500 text-sm">{errors.ph.message}</p>}
          <input {...register("nitrogen", { required: "Nitrogen is required", min: 0 })} type="number" placeholder="Nitrogen (mg/kg)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.nitrogen && <p className="text-red-500 text-sm">{errors.nitrogen.message}</p>}
          <input {...register("potassium", { required: "Potassium is required", min: 0 })} type="number" placeholder="Potassium (mg/kg)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.potassium && <p className="text-red-500 text-sm">{errors.potassium.message}</p>}
          <input {...register("phosphorus", { required: "Phosphorus is required", min: 0 })} type="number" placeholder="Phosphorus (mg/kg)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.phosphorus && <p className="text-red-500 text-sm">{errors.phosphorus.message}</p>}
          <input {...register("organicMatter", { required: "Organic matter is required", min: 0 })} type="number" step="0.1" placeholder="Organic Matter (%)" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.organicMatter && <p className="text-red-500 text-sm">{errors.organicMatter.message}</p>}
          <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700">{t.submit}</button>
        </form>
        {state.soilData && (
          <div className="mt-6">
            <h4 className="text-xl font-semibold mb-4">Results</h4>
            <p>Score: {state.soilData.score}/100</p>
            <p>Recommendation: {state.soilData.recommendation}</p>
          </div>
        )}
      </motion.div>
    );
  };

  // Token Staking Component
  const TokenStaking = () => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const onSubmit = async (data) => {
      if (!isLoggedIn || currentUser.role !== "farmer") return toast.error("Restricted to farmers");
      const crop = state.crops.find(c => c.tokenId === data.tokenId && c.owner === currentUser.email);
      if (!crop) return toast.error("Invalid or unauthorized token");
      const amount = parseFloat(data.amount);
      if (amount <= 0 || amount > crop.quantity) return toast.error("Invalid stake amount");
      try {
        const { success, reward, stakeId } = await hedera.stakeToken(data.tokenId, amount);
        if (!success) throw new Error("Staking failed");
        dispatch({
          type: "UPDATE_CROP",
          index: state.crops.indexOf(crop),
          payload: { ...crop, stakedAmount: crop.stakedAmount + amount },
        });
        dispatch({ type: "UPDATE_BALANCE", amount: reward });
        dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.stake} - ${amount} units`, txId: stakeId, timestamp: new Date().toISOString() } });
        toast.success(`Staked ${amount} units! Reward: ${reward.toFixed(2)} HBAR`);
        reset();
      } catch (err) {
        toast.error(`Staking failed: ${err.message}`);
      }
    };

    const handleBurn = async (tokenId) => {
      const crop = state.crops.find(c => c.tokenId === tokenId && c.owner === currentUser.email);
      if (!crop) return toast.error("Invalid or unauthorized token");
      try {
        const { success, txId } = await hedera.burnToken(tokenId);
        if (!success) throw new Error("Burn failed");
        dispatch({
          type: "UPDATE_CROP",
          index: state.crops.indexOf(crop),
          payload: { ...crop, status: "Burned" },
        });
        dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.burnToken} - ${crop.productName}`, txId, timestamp: new Date().toISOString() } });
        toast.success(`Token burned successfully!`);
      } catch (err) {
        toast.error(`Burn failed: ${err.message}`);
      }
    };

    return (
      <motion.div variants={sectionVariants} custom={9} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
        <div className="flex items-center mb-6">
          <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.staking}</h3>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input {...register("tokenId", { required: "Token ID is required" })} placeholder="Token ID" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.tokenId && <p className="text-red-500 text-sm">{errors.tokenId.message}</p>}
          <input {...register("amount", { required: "Amount is required", min: { value: 1, message: "Minimum 1 unit" } })} type="number" placeholder="Amount to Stake" className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message}</p>}
          <button type="submit" className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500">{t.stake}</button>
        </form>
        <div className="mt-6">
          <h4 className="text-xl font-semibold mb-4">Staked Tokens</h4>
          {state.crops.filter(c => c.stakedAmount > 0 && c.owner === currentUser?.email).length === 0 ? (
            <p className="text-center">{t.noData}</p>
          ) : (
            state.crops.filter(c => c.stakedAmount > 0 && c.owner === currentUser?.email).map((crop, idx) => (
              <div key={idx} className={`p-4 ${darkMode ? "bg-gray-600" : "bg-gray-50"} rounded-lg mb-4`}>
                <p>Token ID: {crop.tokenId}</p>
                <p>Staked Amount: {crop.stakedAmount} units</p>
                <button onClick={() => handleBurn(crop.tokenId)} className="mt-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">{t.burnToken}</button>
              </div>
            ))
          )}
        </div>
      </motion.div>
    );
  };

  // Community Chat Component
  const CommunityChat = () => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm();
    const onSubmit = (data) => {
      if (!isLoggedIn) return toast.error("Login required");
      dispatch({
        type: "ADD_MESSAGE",
        payload: { sender: currentUser.email, content: data.message, timestamp: new Date().toISOString() },
      });
      toast.success("Message sent!");
      reset();
    };

    return (
      <motion.div variants={sectionVariants} custom={10} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
        <div className="flex items-center mb-6">
          <ChatBubbleLeftRightIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.chat}</h3>
        </div>
        <div className="h-64 overflow-y-auto mb-6 p-4 bg-gray-50 rounded-lg">
          {state.messages.length === 0 ? (
            <p className="text-center">{t.noData}</p>
          ) : (
            state.messages.map((msg, idx) => (
              <div key={idx} className={`mb-4 ${msg.sender === currentUser?.email ? "text-right" : "text-left"}`}>
                <p className="text-sm text-gray-500">{msg.sender} - {new Date(msg.timestamp).toLocaleString()}</p>
                <p className={`inline-block p-2 rounded-lg ${msg.sender === currentUser?.email ? "bg-green-600 text-white" : "bg-gray-200 text-gray-900"}`}>{msg.content}</p>
              </div>
            ))
          )}
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="flex space-x-4">
          <input {...register("message", { required: "Message is required" })} placeholder="Type your message..." className="flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          {errors.message && <p className="text-red-500 text-sm">{errors.message.message}</p>}
          <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center">
            <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2" /> {t.sendMessage}
          </button>
        </form>
      </motion.div>
    );
  };

  // Carbon Offset Component
  const CarbonOffset = () => {
    const handleOffset = async () => {
      if (!isLoggedIn) return toast.error("Login required");
      const totalFootprint = state.crops.reduce((sum, crop) => sum + (parseFloat(crop.carbonFootprint) || 0), 0);
      if (totalFootprint <= 0) return toast.info("No carbon footprint to offset");
      const cost = totalFootprint * 0.5; // 0.5 HBAR per ton
      if (cost > state.walletBalance) return toast.error("Insufficient funds");
      try {
        const { success, receipt } = await hedera.transferHBAR(cost);
        if (!success) throw new Error("Offset failed");
        dispatch({ type: "UPDATE_BALANCE", amount: -cost });
        dispatch({ type: "UPDATE_ANALYTICS", payload: { ...state.analytics, carbonFootprint: 0 } });
        dispatch({ type: "ADD_TRANSACTION", payload: { message: `${t.offsetCarbon} - ${totalFootprint} tons`, txId: receipt, timestamp: new Date().toISOString() } });
        toast.success(`Offset ${totalFootprint} tons of carbon for ${cost.toFixed(2)} HBAR`);
      } catch (err) {
        toast.error(`Offset failed: ${err.message}`);
      }
    };

    return (
      <motion.div variants={sectionVariants} custom={11} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
        <div className="flex items-center mb-6">
          <GlobeAmericasIcon className="h-8 w-8 text-green-600 mr-3" /> {/* Replaced LeafIcon with GlobeAmericasIcon */}
          <h3 className="text-3xl font-bold">{t.carbonOffset}</h3>
        </div>
        <p className="text-lg mb-4">Total Carbon Footprint: {state.analytics.carbonFootprint} tons</p>
        <p className="text-md mb-6">Cost to Offset: {(state.analytics.carbonFootprint * 0.5).toFixed(2)} HBAR</p>
        <button onClick={handleOffset} className="w-full bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 flex items-center justify-center">
          <GlobeAmericasIcon className="h-5 w-5 mr-2" /> {/* Replaced LeafIcon with GlobeAmericasIcon */}
          {t.offsetCarbon}
        </button>
      </motion.div>
    );
  };

  // Analytics Component
  const Analytics = () => {
    const rainfallData = {
      labels: ["Jan", "Feb", "Mar", "Apr", "May"],
      datasets: [{
        label: "Rainfall (mm)",
        data: state.analytics.rainfallHistory,
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        fill: true,
      }],
    };

    const priceData = {
      labels: ["Jan", "Feb", "Mar", "Apr", "May"],
      datasets: [
        { label: "Maize", data: state.analytics.priceHistory.maize, borderColor: "rgba(255, 99, 132, 1)", backgroundColor: "rgba(255, 99, 132, 0.2)" },
        { label: "Beans", data: state.analytics.priceHistory.beans, borderColor: "rgba(54, 162, 235, 1)", backgroundColor: "rgba(54, 162, 235, 0.2)" },
        { label: "Tea", data: state.analytics.priceHistory.tea, borderColor: "rgba(255, 206, 86, 1)", backgroundColor: "rgba(255, 206, 86, 0.2)" },
        { label: "Coffee", data: state.analytics.priceHistory.coffee, borderColor: "rgba(75, 192, 192, 1)", backgroundColor: "rgba(75, 192, 192, 0.2)" },
      ],
    };

    const carbonData = {
      labels: ["Maize", "Beans", "Tea", "Coffee"],
      datasets: [{
        label: "Carbon Footprint (tons)",
        data: [10, 5, 8, 2],
        backgroundColor: ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"],
      }],
    };

    return (
      <motion.div variants={sectionVariants} custom={12} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg`}>
        <div className="flex items-center mb-6">
          <ChartBarIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.analytics}</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-lg font-semibold mb-4">Rainfall Trends</h4>
            <Line data={rainfallData} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-lg font-semibold mb-4">Price Trends</h4>
            <Line data={priceData} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-lg font-semibold mb-4">Carbon Footprint Breakdown</h4>
            <Pie data={carbonData} options={{ responsive: true, plugins: { legend: { position: "top" } } }} />
          </div>
        </div>
      </motion.div>
    );
  };

  // Settings Component
  const Settings = () => {
    const handleToggle = (key) => {
      dispatch({ type: "UPDATE_SETTINGS", payload: { ...state.settings, [key]: !state.settings[key] } });
      toast.success("Settings updated!");
    };

    return (
      <motion.div variants={sectionVariants} custom={13} className={`p-8 ${darkMode ? "bg-gray-700 text-white" : "bg-white text-gray-900"} rounded-2xl shadow-lg max-w-lg mx-auto`}>
        <div className="flex items-center mb-6">
          <DocumentTextIcon className="h-8 w-8 text-green-600 mr-3" />
          <h3 className="text-3xl font-bold">{t.settings}</h3>
        </div>
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <span>{t.toggleNotifications}</span>
            <input type="checkbox" checked={state.settings.notifications} onChange={() => handleToggle("notifications")} className="h-5 w-5 text-green-600" />
          </label>
          <label className="flex items-center justify-between">
            <span>{t.autoStake}</span>
            <input type="checkbox" checked={state.settings.autoStake} onChange={() => handleToggle("autoStake")} className="h-5 w-5 text-green-600" />
          </label>
        </div>
      </motion.div>
    );
  };

   // Navigation Tabs
   const NavTabs = () => {
    const tabs = [
      { name: "dashboard", label: t.dashboard, icon: ChartBarIcon },
      { name: "tokenize", label: t.tokenize, icon: GlobeAltIcon, restricted: "farmer" },
      { name: "marketplace", label: t.marketplace, icon: ShoppingCartIcon },
      { name: "p2pTrading", label: t.p2pTrading, icon: ShoppingCartIcon },
      { name: "logistics", label: t.logistics, icon: TruckIcon },
      { name: "insurance", label: t.insurance, icon: ShieldCheckIcon, restricted: "farmer" },
      { name: "aiInsights", label: t.aiInsights, icon: SparklesIcon, restricted: "farmer" },
      { name: "soilAnalysis", label: t.soilAnalysis, icon: BeakerIcon, restricted: "farmer" },
      { name: "staking", label: t.staking, icon: CurrencyDollarIcon, restricted: "farmer" },
      { name: "chat", label: t.chat, icon: ChatBubbleLeftRightIcon },
      { name: "carbonOffset", label: t.carbonOffset, icon: GlobeAmericasIcon }, // Replaced LeafIcon with GlobeAmericasIcon
      { name: "analytics", label: t.analytics, icon: ChartBarIcon },
      { name: "settings", label: t.settings, icon: DocumentTextIcon },
    ];

    return (
      <motion.div variants={sectionVariants} custom={1} className={`p-4 ${darkMode ? "bg-gray-800" : "bg-gray-100"} rounded-lg shadow-lg sticky top-20 z-40`}>
        <div className="flex flex-wrap justify-center space-x-2">
          {tabs.map((tab) => (
            (!tab.restricted || tab.restricted === currentUser?.role) && (
              <button
                key={tab.name}
                onClick={() => setActiveTab(tab.name)}
                className={`flex items-center px-4 py-2 rounded-lg ${activeTab === tab.name ? "bg-green-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"} m-1`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            )
          ))}
        </div>
      </motion.div>
    );
  };

  // Logout Handler
  const handleLogout = () => {
    setIsLoggedIn(false);
    setCurrentUser(null);
    setActiveTab("dashboard");
    toast.success("Logged out successfully!");
  };

  // Fetch Market Data on Mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const weather = await fetchWeatherData();
        const prices = await fetchMarketPrices();
        dispatch({
          type: "UPDATE_MARKET_DATA",
          payload: {
            weather: `Sunny, ${weather.temperature}°C`,
            rainfall: weather.rainfall,
            temperature: weather.temperature,
            humidity: weather.humidity,
            windSpeed: weather.windSpeed,
            ...prices,
          },
        });
        setIsLoading(false);
      } catch (err) {
        toast.error("Failed to fetch market data");
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Main Render
  return (
    <ErrorBoundary>
      <div className={`min-h-screen ${darkMode ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>
        <ToastContainer position="top-right" autoClose={3000} />
        <Header />
        <div className="pt-24 pb-12 max-w-7xl mx-auto px-6">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-600"></div>
            </div>
          ) : (
            <>
              {!isLoggedIn ? (
                <ShowcaseLandingPage />
              ) : (
                <>
                  <NavTabs />
                  {activeTab === "dashboard" && <Dashboard />}
                  {activeTab === "tokenize" && <TokenizeCrop />}
                  {activeTab === "marketplace" && <Marketplace />}
                  {activeTab === "p2pTrading" && <P2PTrading />}
                  {activeTab === "logistics" && <Logistics />}
                  {activeTab === "insurance" && <Insurance />}
                  {activeTab === "aiInsights" && <AIInsights />}
                  {activeTab === "soilAnalysis" && <SoilAnalysis />}
                  {activeTab === "staking" && <TokenStaking />}
                  {activeTab === "chat" && <CommunityChat />}
                  {activeTab === "carbonOffset" && <CarbonOffset />}
                  {activeTab === "analytics" && <Analytics />}
                  {activeTab === "settings" && <Settings />}
                </>
              )}
            </>
          )}
        </div>
        <AuthModal />
      </div>
    </ErrorBoundary>
  );
}

export default App;